import { useApolloClient } from "@apollo/client";
import { formatInvitationPairingCode } from "@msys/common";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguages } from "../../../common/translations/useLanguages.js";
import { ROUTER_BASENAME } from "../../../environment.js";
import { buildRegisterUrlWithPrefill } from "../../auth/buildRegisterUrlWithPrefill.js";
import { SIGNUP_SCOPE } from "../../auth/signup/signupScope.js";
import { MeisterMark } from "../../auth/signup/MeisterMark.js";
import { useAuth } from "../../auth/useAuth.js";
import {
  ONBOARDING_BODY_SX,
  ONBOARDING_PRIMARY_BUTTON_SX,
  ONBOARDING_SMALL_TEXT_BUTTON,
} from "../../features/organisations/onboarding/onboardingDesign.js";
import { REF_TOKEN_URL_SEARCH_PARAM } from "../../constants.js";
import { getCrmLinkingInvitationPrefill } from "../crmLinkingInvitationPrefill.js";
import { InvitationHeading } from "../InvitationHeading.js";
import { InvitationPage } from "../InvitationPage.js";
import {
  RefTokenDocument,
  type RefTokenQuery,
  type RefTokenQueryVariables,
} from "../InvitationTokenProvider.generated.js";
import { resolveInvitationTokenData } from "../resolveInvitationTokenData.js";
import { buildAppUrl } from "./appUrl.js";
import { deviceGrantTokenStorage } from "../../auth/deviceGrantTokenStorage.js";
import { CodeOrScanCard } from "./CodeOrScanCard.js";
import { DeviceSignInView } from "./DeviceSignInView.js";
import { navigateToExternal } from "./navigateToExternal.js";
import { useDeviceSignIn } from "./useDeviceSignIn.js";
import { useInvitationPairing } from "./useInvitationPairing.js";
import { useRenewPasswordlessSignupGrantMutation } from "./useInvitationPairing.generated.js";
import {
  deleteStoredHandover,
  readStoredHandover,
  writeStoredHandover,
  type StoredHandover,
} from "./storedHandover.js";

const REGISTRATION_RETRY_DELAY_MS = 3000;
const HANDOVER_GRANT_URL_PARAM = "msys_handover_grant";
const RESUME_URL_PARAM = "resume";

/**
 * Runs an async step while `active`, retrying every 3 s when it throws (the
 * step is single-use server-side, so a network error must not abandon it).
 * It restarts when `resetKey` changes and stops on unmount.
 */
function useRetryingStep(
  active: boolean,
  step: (isCancelled: () => boolean) => Promise<void>,
  resetKey: string
) {
  const stepRef = React.useRef(step);
  stepRef.current = step;
  React.useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const run = async () => {
      try {
        await stepRef.current(() => cancelled);
      } catch (error) {
        Sentry.captureException(error);
        if (cancelled) return;
        retryTimer = setTimeout(() => {
          void run();
        }, REGISTRATION_RETRY_DELAY_MS);
      }
    };
    void run();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [active, resetKey]);
}

export const ContinueInvitationPage = () => {
  const { t } = useTranslate(["Global"]);
  const { auth, isAuthenticated } = useAuth();
  const { currentLanguage } = useLanguages();
  const client = useApolloClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const resumeRequested = searchParams.get(RESUME_URL_PARAM) === "1";
  // The tab's stored hand-over (a claimed pairing whose registration may need a
  // fresh grant). Read once: later changes are made through the setters below.
  const [handover, setHandover] = React.useState<StoredHandover | null>(() =>
    isAuthenticated ? null : readStoredHandover()
  );
  // True while a renewal-and-redirect is under way (resume link or button).
  const [continuing, setContinuing] = React.useState(
    () => resumeRequested && handover !== null
  );
  const [choiceEmail, setChoiceEmail] = React.useState<string | null>(null);
  const [notContinuable, setNotContinuable] = React.useState(false);
  // No pairing is created or polled behind the not-continuable view; Retry
  // clears the flag and so enables the hook again for a fresh code.
  const pairing = useInvitationPairing({
    enabled: !isAuthenticated && handover === null && !notContinuable,
  });
  const [renewGrant] = useRenewPasswordlessSignupGrantMutation({
    fetchPolicy: "no-cache",
  });
  const renewRef = React.useRef(renewGrant);
  renewRef.current = renewGrant;
  const [view, setView] = React.useState<"pairing" | "device">("pairing");
  const [storageFailed, setStorageFailed] = React.useState(false);
  const deviceSignIn = useDeviceSignIn({
    authServerUrl: auth.authServerUrl ?? "",
    realm: auth.realm ?? "",
    clientId: auth.clientId ?? "",
    onSuccess: tokens => {
      // Tab-scoped; AuthWebProvider picks the tokens up on the reload.
      // The device code is consumed: without stored tokens the reload would
      // land signed out, so show the error state instead of navigating.
      if (!deviceGrantTokenStorage.write(tokens)) {
        setStorageFailed(true);
        return;
      }
      navigateToExternal(buildAppUrl(location.origin, ROUTER_BASENAME, "/"));
    },
  });
  const cancelDeviceSignIn = deviceSignIn.cancel;
  React.useEffect(() => {
    // The claimed pairing wins: stop the device flow so a phone approval
    // cannot navigate the tab away mid-registration.
    if (pairing.status === "claimed" && view === "device") {
      cancelDeviceSignIn();
      setStorageFailed(false);
      setView("pairing");
    }
  }, [pairing.status, view, cancelDeviceSignIn]);
  const canSignInWithPhone = Boolean(
    auth.authServerUrl && auth.realm && auth.clientId
  );

  const lookupInvitation = async (refToken: string) => {
    const { data } = await client.query<RefTokenQuery, RefTokenQueryVariables>({
      query: RefTokenDocument,
      variables: { refToken },
      fetchPolicy: "no-cache",
    });
    const invitation = resolveInvitationTokenData(data);
    // A successful lookup that is not an open CRM linking invitation is
    // permanent (e.g. accepted on the phone meanwhile): do not retry.
    return invitation?.__typename === "CrmLinkingInvitation"
      ? invitation
      : null;
  };
  const lookupRef = React.useRef(lookupInvitation);
  lookupRef.current = lookupInvitation;

  // The one place that sends the browser to Keycloak's registration. The grant
  // goes in before the prefill so that Keycloak's client-note limit drops a
  // prefill parameter, never the grant.
  const startHandoverRegistration = async ({
    refToken,
    grant,
    isCancelled,
  }: {
    refToken: string;
    grant: string | null;
    isCancelled: () => boolean;
  }) => {
    const invitation = await lookupRef.current(refToken);
    if (!invitation) {
      deleteStoredHandover();
      if (!isCancelled()) setNotContinuable(true);
      return;
    }
    const prefill = getCrmLinkingInvitationPrefill(invitation.contact);
    const registerUrl = await auth.createRegisterUrl({
      locale: currentLanguage.locale,
      scope: SIGNUP_SCOPE,
      redirectUri: `${buildAppUrl(location.origin, ROUTER_BASENAME, "/next")}?${REF_TOKEN_URL_SEARCH_PARAM}=${encodeURIComponent(refToken)}`,
    });
    if (isCancelled()) return;
    const url = new URL(registerUrl);
    if (grant) url.searchParams.set(HANDOVER_GRANT_URL_PARAM, grant);
    navigateToExternal(buildRegisterUrlWithPrefill(url.toString(), prefill));
  };
  const startRef = React.useRef(startHandoverRegistration);
  startRef.current = startHandoverRegistration;

  // Signed in: no hand-over is meant to outlive the signup.
  React.useEffect(() => {
    if (isAuthenticated) deleteStoredHandover();
  }, [isAuthenticated]);

  // The resume link is one-shot: drop it so returning to `/continue-invitation`
  // (Back, reopening) shows the choice view instead of renewing again.
  React.useEffect(() => {
    if (!resumeRequested) return;
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete(RESUME_URL_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [resumeRequested, setSearchParams]);

  const claimedRefToken = pairing.refToken;
  // CLAIMED: remember the hand-over (tab-scoped) so an expired registration
  // can renew its grant.
  const { status: pairingStatus, pairingId, secret } = pairing;
  React.useEffect(() => {
    if (
      pairingStatus !== "claimed" ||
      !pairingId ||
      !secret ||
      !claimedRefToken
    ) {
      return;
    }
    writeStoredHandover({
      pairingId,
      secret,
      refToken: claimedRefToken,
      savedAt: Date.now(),
    });
  }, [pairingStatus, pairingId, secret, claimedRefToken]);

  // The claim is single-use server-side, so a failure here must be retried
  // rather than abandoned while the page is mounted and the refToken is held.
  const claimedGrant = pairing.passwordlessSignupGrant;
  useRetryingStep(
    pairing.status === "claimed" && Boolean(claimedRefToken) && !notContinuable,
    async isCancelled => {
      if (!claimedRefToken) return;
      await startRef.current({
        refToken: claimedRefToken,
        grant: claimedGrant,
        isCancelled,
      });
    },
    `${claimedRefToken}|${claimedGrant}`
  );

  // Resume (link or button): renew the grant, then redirect with it.
  useRetryingStep(
    continuing && handover !== null && !notContinuable,
    async isCancelled => {
      if (!handover) return;
      const result = await renewRef.current({
        variables: {
          input: {
            pairingId: handover.pairingId,
            secret: handover.secret,
            refToken: handover.refToken,
          },
        },
      });
      if (isCancelled()) return;
      const renewal = result.data?.renewPasswordlessSignupGrant;
      if (!renewal) throw new Error("No renewal returned");
      if (renewal.status === "UNAVAILABLE") {
        deleteStoredHandover();
        setHandover(null);
        setContinuing(false);
        setNotContinuable(true);
        return;
      }
      await startRef.current({
        refToken: handover.refToken,
        grant: renewal.passwordlessSignupGrant ?? null,
        isCancelled,
      });
    },
    `${handover?.pairingId}|${handover?.refToken}`
  );

  // Choice view (no resume link): needs the invitation's email.
  useRetryingStep(
    handover !== null && !continuing && !notContinuable && !isAuthenticated,
    async isCancelled => {
      if (!handover) return;
      const invitation = await lookupRef.current(handover.refToken);
      if (isCancelled()) return;
      if (!invitation) {
        // Accepted or withdrawn meanwhile: nothing left to continue.
        deleteStoredHandover();
        setHandover(null);
        setNotContinuable(true);
        return;
      }
      setChoiceEmail(getCrmLinkingInvitationPrefill(invitation.contact).email);
    },
    `${handover?.refToken}`
  );

  const discardHandover = () => {
    deleteStoredHandover();
    setHandover(null);
    setContinuing(false);
    setChoiceEmail(null);
  };

  const formattedCode = pairing.code
    ? formatInvitationPairingCode(pairing.code)
    : null;

  const body = (text: string) => (
    <Typography
      sx={{
        ...ONBOARDING_BODY_SX,
        mt: "11px",
        width: "100%",
        textWrap: "balance",
      }}
    >
      {text}
    </Typography>
  );

  return (
    // Shown on a fair-stand PC and read from a few metres, so the code and the
    // QR code get the room: one card, side by side on a desktop, stacked below.
    <InvitationPage isAuthenticated={isAuthenticated} contentMaxWidth={800}>
      <Stack sx={{ alignItems: "center" }}>
        <MeisterMark />
        <InvitationHeading sx={{ mt: "25px" }}>
          {view === "device" && !isAuthenticated && pairing.status !== "claimed"
            ? t("Sign in with your phone", { ns: "Global" })
            : t("Continue invitation", { ns: "Global" })}
        </InvitationHeading>
        {isAuthenticated ? (
          <Stack sx={{ width: "100%", maxWidth: 500 }}>
            {body(
              t(
                "You are signed in. Sign out to show a code for the next invitation.",
                { ns: "Global" }
              )
            )}
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                void auth.logout({
                  redirectUri: buildAppUrl(
                    location.origin,
                    ROUTER_BASENAME,
                    "/continue-invitation"
                  ),
                });
              }}
              sx={{ ...ONBOARDING_PRIMARY_BUTTON_SX, mt: "28px" }}
            >
              {t("Sign out and show a new code", { ns: "Global" })}
            </Button>
          </Stack>
        ) : notContinuable ? (
          <Stack sx={{ width: "100%", maxWidth: 500 }}>
            {body(
              t(
                "This invitation can no longer be continued on another device.",
                { ns: "Global" }
              )
            )}
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                setNotContinuable(false);
                discardHandover();
                pairing.restart();
              }}
              sx={{ ...ONBOARDING_PRIMARY_BUTTON_SX, mt: "28px" }}
            >
              {t("Retry", { ns: "Global" })}
            </Button>
          </Stack>
        ) : pairing.status === "claimed" || (handover && continuing) ? (
          body(
            t("Invitation received. Starting registration…", { ns: "Global" })
          )
        ) : handover ? (
          choiceEmail === null ? (
            <Stack sx={{ alignItems: "center", mt: "28px" }}>
              <CircularProgress aria-label={t("Loading…", { ns: "Global" })} />
            </Stack>
          ) : (
            <Stack sx={{ width: "100%", maxWidth: 500 }}>
              {body(
                t("Continue registration for {email}?", {
                  ns: "Global",
                  email: choiceEmail,
                })
              )}
              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={() => {
                  setContinuing(true);
                }}
                sx={{ ...ONBOARDING_PRIMARY_BUTTON_SX, mt: "28px" }}
              >
                {t("Continue registration", { ns: "Global" })}
              </Button>
              <Button
                variant="text"
                onClick={discardHandover}
                sx={{ ...ONBOARDING_SMALL_TEXT_BUTTON, mt: "16px" }}
              >
                {t("Show a new code", { ns: "Global" })}
              </Button>
            </Stack>
          )
        ) : view === "device" ? (
          <DeviceSignInView
            state={storageFailed ? { status: "error" } : deviceSignIn.state}
            onRestart={() => {
              setStorageFailed(false);
              deviceSignIn.start();
            }}
            onBack={() => {
              setStorageFailed(false);
              deviceSignIn.cancel();
              setView("pairing");
            }}
          />
        ) : (
          <>
            {body(
              t(
                "Open the invitation link from your email on your phone and choose “Continue on another device”.",
                { ns: "Global" }
              )
            )}
            {formattedCode && pairing.code ? (
              <Box sx={{ width: "100%", mt: "32px" }}>
                <CodeOrScanCard
                  code={formattedCode}
                  qrValue={`${buildAppUrl(
                    location.origin,
                    ROUTER_BASENAME,
                    "/continue-invitation/join"
                  )}?code=${pairing.code}`}
                  data-testid="continue-invitation-code"
                />
              </Box>
            ) : pairing.status === "error" ? (
              <Typography
                color="error"
                sx={{ mt: "11px", textAlign: "center" }}
              >
                {t("The code could not be created. Trying again…", {
                  ns: "Global",
                })}
              </Typography>
            ) : null}
            {canSignInWithPhone ? (
              // A line of text and a small text button, like the signup
              // page's "Already have an account? Log in".
              <Typography
                component="div"
                sx={{
                  ...ONBOARDING_BODY_SX,
                  mt: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  columnGap: "4px",
                  fontSize: 14,
                }}
              >
                {t("Already registered on your phone?", { ns: "Global" })}
                <Button
                  variant="text"
                  data-testid="device-sign-in-start"
                  onClick={() => {
                    setView("device");
                    setStorageFailed(false);
                    deviceSignIn.start();
                  }}
                  sx={ONBOARDING_SMALL_TEXT_BUTTON}
                >
                  {t("Sign in with your phone", { ns: "Global" })}
                </Button>
              </Typography>
            ) : null}
          </>
        )}
      </Stack>
    </InvitationPage>
  );
};
